import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { ThemeProvider, useTheme } from "@/lib/theme-context";
import LoginPage from "@/pages/LoginPage";
import AdminShell from "@/components/layout/AdminShell";
import PosShell from "@/components/layout/PosShell";
import DashboardPage from "@/pages/admin/DashboardPage";
import BancasPage from "@/pages/admin/BancasPage";
import GruposPage from "@/pages/admin/GruposPage";
import AgenciasPage from "@/pages/admin/AgenciasPage";
import TaquillasPage from "@/pages/admin/TaquillasPage";
import VendedoresPage from "@/pages/admin/VendedoresPage";
import LoteriasPage from "@/pages/admin/LoteriasPage";
import SorteosPage from "@/pages/admin/SorteosPage";
import CuposPage from "@/pages/admin/CuposPage";
import ReportesPage from "@/pages/admin/ReportesPage";
import EkiipagoPage from "@/pages/admin/EkiipagoPage";
import AuditPage from "@/pages/admin/AuditPage";
import UpdatesPage from "@/pages/admin/UpdatesPage";
import TaquillaMadrePage from "@/pages/admin/TaquillaMadrePage";
import PosSalesPage from "@/pages/pos/PosSalesPage";
import PosRechargesPage from "@/pages/pos/PosRechargesPage";
import PosSegurosPage from "@/pages/pos/PosSegurosPage";
import PosValidatorPage from "@/pages/pos/PosValidatorPage";
import PosHistoryPage from "@/pages/pos/PosHistoryPage";
import PublicValidatorPage from "@/pages/PublicValidatorPage";
import KioscoPage from "@/pages/kiosco/KioscoPage";
import InstallPWAPrompt from "@/components/InstallPWAPrompt";
import "@/App.css";

const MADRE = "taquilla_madre_operator";

function MadreOnly({ children }) {
  const { user } = useAuth();
  return user?.role === MADRE ? children : <Navigate to="/admin" replace />;
}

function Protected({ roles, children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-6 text-muted-foreground">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "vendedor" || user.role === "taquilla_operator" ? "/pos" : "/admin"} replace />;
  }
  return children;
}

function RoleRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-6 text-muted-foreground">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "vendedor" || user.role === "taquilla_operator") return <Navigate to="/pos" replace />;
  if (user.role === "grupo_owner" || user.role === "agencia_owner") return <Navigate to="/admin/reportes" replace />;
  if (user.role === MADRE) return <Navigate to="/admin/caja-00" replace />;
  return <Navigate to="/admin" replace />;
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-right" richColors />;
}

function NoMadreOutsideCaja({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user?.role === MADRE && !pathname.startsWith("/admin/caja-00")) return <Navigate to="/admin/caja-00" replace />;
  return children;
}

function AdminIndex() {
  const { user } = useAuth();
  if (user?.role === "grupo_owner" || user?.role === "agencia_owner") return <Navigate to="/admin/reportes" replace />;
  if (user?.role === MADRE) return <Navigate to="/admin/caja-00" replace />;
  return <DashboardPage />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/validar/:code" element={<PublicValidatorPage />} />
            <Route path="/kiosco/:taquillaId" element={<KioscoPage />} />

            <Route
              path="/admin"
              element={
                <Protected roles={["admin", "banca_owner", "grupo_owner", "agencia_owner", MADRE]}>
                  <NoMadreOutsideCaja><AdminShell /></NoMadreOutsideCaja>
                </Protected>
              }
            >
              <Route index element={<AdminIndex />} />
              <Route path="caja-00" element={<MadreOnly><TaquillaMadrePage /></MadreOnly>} />
              <Route path="caja-00/tickets" element={<MadreOnly><TaquillaMadrePage view="tickets" /></MadreOnly>} />
              <Route path="caja-00/cuadre" element={<MadreOnly><TaquillaMadrePage view="cuadre" /></MadreOnly>} />
              <Route path="bancas" element={<BancasPage />} />
              <Route path="grupos" element={<GruposPage />} />
              <Route path="agencias" element={<AgenciasPage />} />
              <Route path="taquillas" element={<TaquillasPage />} />
              <Route path="vendedores" element={<VendedoresPage />} />
              <Route path="loterias" element={<LoteriasPage />} />
              <Route path="sorteos" element={<SorteosPage />} />
              <Route path="cupos" element={<CuposPage />} />
              <Route path="servicios" element={<EkiipagoPage />} />
              <Route path="reportes" element={<ReportesPage />} />
              <Route path="auditoria" element={<AuditPage />} />
              <Route path="updates" element={<UpdatesPage />} />
            </Route>

            <Route
              path="/pos"
              element={
                <Protected roles={["vendedor", "taquilla_operator", "admin"]}>
                  <PosShell />
                </Protected>
              }
            >
              <Route index element={<PosSalesPage />} />
              <Route path="recargas" element={<PosRechargesPage />} />
              <Route path="seguros" element={<PosSegurosPage />} />
              <Route path="validar" element={<PosValidatorPage />} />
              <Route path="historial" element={<PosHistoryPage />} />
            </Route>

            <Route path="/" element={<RoleRedirect />} />
            <Route path="*" element={<RoleRedirect />} />
          </Routes>
        </BrowserRouter>
        <ThemedToaster />
        <InstallPWAPrompt />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
